import type { FileEntry } from 'bz-block-template/components/file-upload/useFileUploadState';
import type Trace from 'bz-clinical/stores/trace';

import _ from 'lodash';

import { action, computed, observable, observe, toJS } from 'mobx';

import { convertFromRaw, EditorState, Modifier, RichUtils } from 'tm2-draft-js';

import type { Psfs } from 'tm3-core/api.dtos';

import ValidatedEnityControl from '../draft/entities/validated-control';
import TemplateStore from './template-store';
import ValidationStore from './validation-store';

export type TOnAction = (action: string, request: any) => void;

class FormStore extends ValidationStore {
  @observable mode = 'live';
  @observable highlight = false;
  @observable overselect = false;
  @observable summary: string[] = [];
  @observable snippet = [];
  @observable lists = {};
  @observable lookups = {};
  @observable searches = {};
  @observable readOnly = false;
  @observable loading = true;
  @observable onReferralPortal = false;
  @observable scoreTypes = [];
  @observable isDirty: boolean = false;
  @observable isSubmitting: boolean = false;
  @observable readonly autoPopulationKeys: Map<string, string[]> = new Map<string, string[]>();
  onConnect = false;
  @observable
  isEdittingFormTemplates = false;
  @observable
  onAction: TOnAction = () => {};
  onHighlight: (highlight: Record<string, number[]>) => void = () => {};
  onDataChange: (field: string, value: any, trace: Trace, options: any) => void = () => {};
  onKeyQuestion = () => {};
  @observable psfs = observable.array<Psfs>([]);
  @observable signature: string | undefined;
  @observable formId: string | undefined;
  @observable readonly fileEntriesByField = observable.map<string, FileEntry[]>();

  @computed
  get hasPendingFiles(): boolean {
    for (const entries of this.fileEntriesByField.values()) {
      if (entries.some((e) => e.status === 'uploading' || e.status === 'removing')) return true;
    }
    return false;
  }

  public getFileEntries(fieldId: string): FileEntry[] {
    return this.fileEntriesByField.get(fieldId) ?? [];
  }

  @action.bound
  addFileEntry(fieldId: string, entry: FileEntry) {
    const list = this.fileEntriesByField.get(fieldId) ?? [];
    this.fileEntriesByField.set(fieldId, [...list, entry]);
  }

  @action.bound
  updateFileEntry(fieldId: string, key: string, patch: Partial<FileEntry>) {
    const list = this.fileEntriesByField.get(fieldId);
    if (!list) return;
    this.fileEntriesByField.set(
      fieldId,
      list.map((f) => (f.key === key ? { ...f, ...patch } : f))
    );
  }

  @action.bound
  removeFileEntry(fieldId: string, key: string) {
    const list = this.fileEntriesByField.get(fieldId);
    if (!list) return;
    this.fileEntriesByField.set(
      fieldId,
      list.filter((f) => f.key !== key)
    );
  }

  @action.bound
  clearFileEntries(fieldId: string) {
    this.fileEntriesByField.delete(fieldId);
  }

  constructor(props) {
    super(props);
    this.onDebounce = _.debounce((field) => {
      this.onValidate(field).catch(() => {});
    }, 500);
    observe(this, 'highlight', (res) => {
      if (res) {
        return;
      }
      this.editor = EditorState.moveSelectionToEnd(this.editor);
    });
  }

  @action
  load(template?: unknown, data?: unknown, type?: unknown) {
    if (!!type) {
      this.type = type;
    }
    if (template && template.sections) {
      // caseflow templates only...
      this.editor = TemplateStore.load(null, this.decoratorFn());
      template = this.buildTemplate(toJS(template));
      template.sections.forEach(({ body }) => {
        data = TemplateStore.map(body, data);
        this.insertTemplate(body);
        this.buildAutoPopulationKeys(body);
      });
      this.dataAsMap.replace(data);
    } else {
      this.editor = TemplateStore.load(template, this.decoratorFn());
      this.dataAsMap.replace(TemplateStore.map(template, data));
    }
    if (template && template.lists) {
      // caseflow templates only...
      let lists = { _idToNameMap: {} };
      Object.values(template.lists).forEach((list) => {
        // On pronto the global id is the Id.
        let id = list.globalId;
        if (id === undefined) {
          id = list.id;
        }
        lists._idToNameMap[list.name] = id;
        lists[id] = JSON.parse(list.value);
      });
      this.lists = lists;
    }
    this.updateTemplate();
    this.onRegulate();
    this.onValidate();
    this.loading = false;
  }

  @action.bound
  onChange(field, value, options, aiFilled: boolean = false) {
    this.dataAsMap.set(field, value);

    if (aiFilled) {
      this.aiFilledFields.add(field);
    } else {
      this.aiFilledFields.delete(field);
    }

    this.onDataChange(field, value, undefined, options); // undefined = trace as we want it to be made in the function
    this.onDebounce(field);
    this.isDirty = true;
    //this.onValidate(field).catch(() => {}) //supress validation logging
  }

  @action
  onSelect(key, blockKey) {
    let selection = {
      anchorKey: blockKey,
      focusKey: blockKey,
      hasFocus: true,
      isBackward: false
    };
    let content = this.editor.getCurrentContent();
    let block = content.getBlockForKey(blockKey);
    let range = block.findEntityRanges(
      (x) => x.entity === key,
      (start, finish) => {
        selection.focusOffset = finish;
        selection.anchorOffset = start;
      }
    );

    selection = this.editor.getSelection().merge(selection);
    this.editor = this.applyHighlight(selection);
  }

  @action
  onDragStart(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  @action
  insertTemplate(template) {
    let { editor } = this;
    let content = editor.getCurrentContent();
    let selection = editor.getSelection();
    let blockMap = convertFromRaw(template).getBlockMap();
    content = Modifier.replaceWithFragment(content, selection, blockMap);
    editor = RichUtils.insertSoftNewline(editor);
    this.editor = EditorState.push(editor, content, 'insert-fragment');
  }

  @action
  insertTemplateAndUpdate(template) {
    this.insertTemplate(template);
    this.updateTemplate();
  }

  onSnippet() {
    let { editor } = this;
    let content = editor.getCurrentContent();
    let selection = editor.getSelection();

    let anchor = {
      key: selection.getAnchorKey(),
      offset: selection.getAnchorOffset()
    };

    let focus = {
      key: selection.getFocusKey(),
      offset: selection.getFocusOffset()
    };

    let start = selection.isBackward ? focus : anchor;
    let finish = selection.isBackward ? anchor : focus;

    let truncLeft = editor.getSelection().merge({
      anchorKey: start.key,
      focusKey: start.key,
      anchorOffset: 0,
      focusOffset: start.offset,
      isBackward: false
    });

    let truncRight = editor.getSelection().merge({
      anchorKey: finish.key,
      focusKey: finish.key,
      anchorOffset: finish.offset,
      focusOffset: finish.offset
    });

    content = Modifier.replaceText(content, truncLeft, '');
    content = Modifier.splitBlock(content, truncRight);

    let block = content.getBlockForKey(start.key);
    let blocks = [];

    do {
      blocks.push(block);
      block = content.getBlockAfter(block.key);
    } while (blocks[blocks.length - 1].key !== finish.key && !!block);

    this.snippet = blocks;
    if (this.onPublishSnippet) {
      this.onPublishSnippet(this.snippet);
    }
  }

  @action
  onTemplate(state) {
    let s = state.getSelection();

    this.editor = this.highlight && s.hasFocus && !s.isCollapsed() ? this.applyHighlight(s) : state;
  }

  @action
  applyHighlight(selection) {
    let state = EditorState.acceptSelection(this.editor, selection);
    let style = this.mode === 'live' ? 'HIGHLIGHT' : 'SUMMARY';
    state = RichUtils.toggleInlineStyle(state, style);

    selection = selection.merge({
      anchorKey: selection.getFocusKey(),
      anchorOffset: selection.getFocusOffset()
    });

    state = EditorState.acceptSelection(state, selection);

    let result = {};
    state
      .getCurrentContent()
      .getBlocksAsArray()
      .map((block) => {
        let range = [];
        block.findStyleRanges(
          (x) => {
            return x.hasStyle('HIGHLIGHT');
          },
          (start, end) => {
            let last = range.length - 1;
            if (range[last] === start) {
              range[last] = end;
            } else {
              range.push(start, end);
            }
          }
        );
        if (range.length > 0) {
          result[block.key] = range;
        }
      });

    this.onHighlight(result);
    return state;
  }

  controlFn(props) {
    // Hack to get validation for nested properties

    return (
      <ValidatedEnityControl
        aiFilledFields={this.aiFilledFields as unknown as Set<string>}
        attributes={props.attributes}
        cache={this.cache}
        errored={!!this.errors[props.field]}
        errors={this.errors}
        children={props.children}
        control={props.control}
        field={props.field}
        inline={props.inline || false}
        lists={this.lists}
        lookups={this.lookups}
        psfs={this.psfs}
        readOnly={this.readOnly}
        searches={this.searches}
        value={this.dataAsMap.get(props.field)}
        valueOther={this.dataAsMap.get(props.field + '-explanation')}
        warning={props.attributes.required === true}
        onChangeOther={this.onChange.bind(this, props.field + '-explanation')}
        onSelect={this.onSelect.bind(this, props.key, props.blockKey)}
        onDragStart={this.onDragStart.bind(this)}
        onChange={this.onChange.bind(this, props.field)}
        onAction={this.onAction.bind(this)}
        onReferralPortal={this.onReferralPortal}
        onConnect={this.onConnect}
        formId={this.formId}
      />
    );
  }

  findEntity(entityMap, fieldName) {
    return Object.keys(entityMap)
      .filter((x) => entityMap[x].data && entityMap[x].data.field === fieldName)
      .map((x) => entityMap[x])
      .find(() => true); // hack to get first element or null
  }

  buildTemplate(t) {
    let template = Object.assign({}, t);
    template.sections = t.sections.map((s) => {
      let section = Object.assign({}, s);

      if (typeof section.body === 'string') {
        section.body = JSON.parse(section.body || '{}');
      }
      this.applyMandatoryFields(section, template);
      this.applyCustomLabels(section, template);
      this.applyCustomLists(section, template);
      return section;
    });
    return template;
  }

  applyMandatoryFields(section, template) {
    if (!template.mandatoryFieldOverrides) {
      template.mandatoryFieldOverrides = {};
    }

    let mandatoryFieldOverrides = template.mandatoryFieldOverrides[section.id];
    if (mandatoryFieldOverrides) {
      Object.keys(mandatoryFieldOverrides).forEach((o) => {
        let entity = this.findEntity(section.body.entityMap, o);
        if (entity) {
          entity.data.required = mandatoryFieldOverrides[o];
        }
      });
    }
  }

  applyCustomLabels(section, template) {
    if (!template.labelOverrides) {
      template.labelOverrides = {};
    }
    let labelOverrides = template.labelOverrides[section.id];
    if (labelOverrides) {
      Object.keys(labelOverrides).forEach((o) => {
        let entity = this.findEntity(section.body.entityMap, o);
        if (entity) {
          if (entity.type === 'Control-Label') {
            entity.data.text = labelOverrides[o];
          } else {
            entity.data.label = labelOverrides[o];
          }
        }
      });
    }
  }

  applyCustomLists(section, template) {
    if (!template.customLists) {
      template.customLists = {};
    }
    let customLists = template.customLists[section.id];
    if (customLists) {
      Object.keys(customLists).forEach((o) => {
        let entity = this.findEntity(section.body.entityMap, o);
        if (entity) {
          entity.data.src = customLists[o];
        }
      });
    }
  }

  buildAutoPopulationKeys(body) {
    Array.from(Object.values(body.entityMap))
      .filter((e) => !!e.data.autopopkey)
      .forEach((e) => {
        const arr = (this.autoPopulationKeys.get(e.data.autopopkey) ?? []).addItems(e.data.field);
        this.autoPopulationKeys.set(e.data.autopopkey, arr);
      });
  }
}

export default FormStore;
